// Note. The task to correctly type all type of messages we get from BE
// is quite big. I do not have enough time to fully do it as part of this
// ticket. But I want to start.
export type AttachmentPayloadType = 'generic' | 'button' | 'list';

export type AttachmentButtonType = 'postback';

export type AttachmentButton = {
  type: AttachmentButtonType;
  title: string;
  payload?: string;
  url?: string;
  webviewHeightRatio?: string;
};

export type QuickReply = {
  contentType: 'user_email' | 'text';
  title?: string;
  payload?: string;
};

export type AttachmentPayload = {
  templateType: AttachmentPayloadType;
  text: string;
  topElementStyle: string;
  buttons: AttachmentButton[];
};

export type AttachmentType = 'template';

export type Attachment = {
  type: AttachmentType;
  payload: AttachmentPayload;
};

export enum MessageKind {
  unknown = 1,
  message = 2,
  quickReply = 3,
  welcome = 4,
  defaultAnswer = 5,
  senderAction = 6,
  referral = 7,
  postback = 8,
  optin = 9,
  igStory = 10,
  igComment = 11,
  liveComment = 13,
  reaction = 100,
  delete = 101,
  read = 102,
  delivery = 103,
  fail = 104,
  linkClicked = 105,
}

export enum ConversationDirection {
  incoming = 1,
  outgoing = 2,
}

export type MessageStructureButtonBase = {
  payload?: string;
  title: string;
};

export type MessageStructureQuickReply = MessageStructureButtonBase & {
  contentType: 'text' | 'user_email' | 'user_phone_number' | 'location';
  imageUrl: string;
};

export type MessageStructureButton = MessageStructureButtonBase & {
  type: 'postback' | 'web_url' | 'phone_number' | 'element_share';
  url?: string;
  webviewHeightRatio?: any;
};

export type MessageStructureGalleryElement = {
  title: string;
  subtitle?: string;
  imageUrl: string;
  buttons?: MessageStructureButton[];
  defaultAction?: {
    type: 'web_url';
    url: string;
  };
};

export type MessageStructurePayloadAttachment = {
  payload?: {
    buttons?: MessageStructureButton[];
    templateType?: 'button' | 'generic';
    text?: string;
    url?: string;
    isReusable?: boolean;
    attachmentId?: string;
    elements?: MessageStructureGalleryElement[];
    imageAspectRatio?: 'square' | 'horizontal';
  };
  type:
    | 'template'
    | 'image'
    | 'file'
    | 'video'
    | 'audio'
    | 'like_heart'
    | 'share'
    | 'story_mention';
};

export type MessageStuctureOTNRequestAttachment = {
  type: 'template';
  payload: {
    templateType: 'one_time_notif_req';
    title: string;
    text: undefined;
    url: undefined;
    buttons: null;
  };
};

export type MessageStructureFallbackAttachment = {
  type: 'fallback';
  title: string;
  url: string;
  payload: null | {
    title: string;
    url: string;
    text: null;
    buttons: null;
  };
};

export type MessageStructureStory = {
  mention: {
    link: string;
  };
  replyTo: {
    id: string;
    link: string;
  };
};

export type MessageStructureAttachment =
  | MessageStructurePayloadAttachment
  | MessageStuctureOTNRequestAttachment
  | MessageStructureFallbackAttachment;

type Referral = {
  source?:
    | 'ADS'
    | 'CUSTOMER_CHAT_PLUGIN'
    | 'SHORTLINK'
    | 'MOBILEMONKEY_APP_EMBEDDED_CHAT';
  url?: string; // Webchat specific payload
  currentPlan?: string;
  subscriptionAdminLink?: string;
  facebookPageName?: string;
  facebookPageAdminLink?: string;
  companyName?: string;
  companyAdminLink?: string;
  leadType?: string | null;
};

type MessageTags = {
  source?: string;
};

export type MessageCampaignAttributes = {
  name?: string;
  purpose?: 'none' | 'global' | 'drip' | 'rss_feed' | 'timezone' | 'automation';
};

export type MessageStructure = {
  fbGuardName?: string;
  text?: string;
  title?: string;
  quickReplies?: MessageStructureQuickReply[];
  attachment?: MessageStructureAttachment;
  attachments?: MessageStructureAttachment[];
  quickReply?: Partial<MessageStructureQuickReply>;
  payload?: string;
  referral?: Referral;
  seconds?: number;
  senderAction?: 'typing_on' | 'delay';
  tags?: MessageTags;
  source?: 'ADS' | 'CUSTOMER_CHAT_PLUGIN' | 'SHORTLINK' | 'FB_COMMENT_GUARD'; // Not sure if I found all possible values
  type?: 'OPEN_THREAD' | 'one_time_notif_req'; // Not sure if I found all possible values
  adId?: string;
  smAdId?: string;
  smRemoteAdId?: string;
  lcMessageId?: string;
  clientMessageId?: string;
  ref?: string;
  promoterKind?: string;
  promoterName?: string;
  url?: string; // Webchat was initiated from this URL
  lcKind?: string;
  emoji?: string;
  story?: MessageStructureStory;
  isEcho?: boolean;
  mid?: string;
  replyTo?: {
    mid: string;
  };
  subject?: string;
  body?: string;
  previewText?: string;
  hiddenBody?: string;
  link?: string; // link clicked email event - text of link
  target?: string; // link clicked email event - url of link
  emailTimestamp?: string; // email opened event - timestamp of email message
  emailAttachmentIds?: number[];
  emailAttachmentTitles?: string[];
};

export type IgMessageStructure = {
  mid: string;
} & Pick<MessageStructure, 'attachment' | 'attachments' | 'text'>;

export type LivechatMessageModel = {
  conversationId: number;
  conversationSessionId: number;
  remoteUserId: string;
  firstName: string;
  lastName: string;
  time: string; // Timestamp is string
  kind: MessageKind;
  direction: ConversationDirection;
  campaign?: MessageCampaignAttributes;
  structure: MessageStructure;
  personaId: string | null;
  currentPageId?: number;
  mutedAt?: number;
  muted?: boolean;
  liveChatResponse?: boolean;
  liveChatUserId?: string;
  botId: string;
  pageBeginning?: boolean;
  mid?: string;
  withDeliveryError: boolean;
  // Note: for now we do not really the structure and intend to display as text first.
  // Later additional parsing might be helpfult.
  responseStructure: string;
  accountData?: string; // BE is storing email account used to send email
  readAt?: string;
};
